import { useState, type CSSProperties, type FormEvent } from 'react';
import { getCabinetId, type EasterEggId } from '../lib/easterEggProgress';

interface ChampionUnlockProps {
  existingName?: string;
  onComplete: (rank: number, playerName: string) => void;
  onReturn: () => void;
  unlocks: EasterEggId[];
}

export function ChampionUnlock({ existingName, onComplete, onReturn, unlocks }: ChampionUnlockProps) {
  const [playerName, setPlayerName] = useState(existingName ?? '');
  const [phase, setPhase] = useState<'form' | 'sending' | 'winner' | 'error'>('form');
  const [error, setError] = useState('');
  const [result, setResult] = useState<{ rank: number; title: string } | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const name = playerName.trim();
    if (name.length < 2 || name.length > 32) {
      setError('Use 2–32 characters for the champion name.');
      setPhase('error');
      return;
    }
    setError('');
    setPhase('sending');
    try {
      const response = await fetch('/api/easter-egg-champion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cabinetId: getCabinetId(), playerName: name, unlocks, website: '' }),
      });
      const payload = await response.json() as { rank?: number; title?: string; error?: string };
      if (!response.ok || !payload.rank || !payload.title) throw new Error(payload.error ?? 'champion-link-failed');
      setResult({ rank: payload.rank, title: payload.title });
      setPhase('winner');
      onComplete(payload.rank, name);
    } catch {
      setError('Your cabinet win is saved here, but the championship channel did not answer. Try again later.');
      setPhase('error');
    }
  };

  return (
    <div className="champion-unlock" role="dialog" aria-modal="true" aria-labelledby="champion-unlock-title">
      <div className="champion-unlock__fireworks" aria-hidden="true">
        {Array.from({ length: 28 }, (_, index) => <i key={index} style={{ '--spark': index } as CSSProperties} />)}
      </div>
      <section className="champion-unlock__panel">
        <p>ALL THREE SECRET SIGNALS CONFIRMED</p>
        <h1 id="champion-unlock-title">CABINET CHAMPION</h1>
        {phase === 'winner' && result ? (
          <div className="champion-unlock__result" aria-live="assertive">
            <strong>{result.title}</strong>
            <span>GLOBAL FINISH // #{result.rank}</span>
            <b>{playerName.trim()} WON THE WHOLE FIGHT</b>
            <button type="button" onClick={onReturn}>RETURN TO ATTRACT MODE</button>
          </div>
        ) : (
          <form onSubmit={submit}>
            <label htmlFor="cabinet-champion-name">Champion name</label>
            <input id="cabinet-champion-name" value={playerName} onChange={(event) => setPlayerName(event.target.value)} maxLength={32} autoComplete="name" autoFocus disabled={phase === 'sending'} />
            <button type="submit" disabled={phase === 'sending'}>{phase === 'sending' ? 'VERIFYING…' : 'CLAIM CABINET RANK'}</button>
            {error && <p className="champion-unlock__error" role="alert">{error}</p>}
            <button className="champion-unlock__return" type="button" onClick={onReturn}>RETURN TO ATTRACT MODE</button>
          </form>
        )}
      </section>
    </div>
  );
}
